import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronDown, ChevronUp, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { useOnboardingContext } from '@/contexts/onboarding-context';

interface CtaConfig {
  label: string;
  getHref: (projectId: string | null) => string;
  variant?: 'primary' | 'secondary';
}

interface StepConfig {
  id: 'start_project' | 'edit_project' | 'publish_share';
  title: string;
  description: string;
  ctas: CtaConfig[];
}

const STEPS: StepConfig[] = [
  {
    id: 'start_project',
    title: 'Create your first project',
    description: 'Type a prompt in the chat to build with AI, create a blank project, or browse the Prompt Library for inspiration.',
    ctas: [
      {
        label: 'Create project',
        getHref: () => '/dashboard/editor?openCreateModal=true',
        variant: 'primary',
      },
      {
        label: 'Prompt Library',
        getHref: () => '/dashboard/prompt-library',
        variant: 'secondary',
      },
    ],
  },
  {
    id: 'edit_project',
    title: 'Make your first edit',
    description: 'Open your project and ask the AI Agent to make changes.',
    ctas: [
      {
        label: 'Open editor',
        getHref: (projectId) => projectId ? `/dashboard/editor/${projectId}` : '/dashboard',
        variant: 'primary',
      },
    ],
  },
  {
    id: 'publish_share',
    title: 'Publish your app',
    description: 'Deploy your app and share it with the world.',
    ctas: [
      {
        label: 'Publish',
        getHref: (projectId) => projectId ? `/dashboard/editor/${projectId}?tab=publish` : '/dashboard',
        variant: 'primary',
      },
    ],
  },
];

export function OnboardingChecklist() {
  const navigate = useNavigate();
  const {
    showChecklist,
    completedSteps,
    prerequisites,
    recommendedStep,
    lastActiveProjectId,
    isLoading,
    dismiss,
  } = useOnboardingContext();

  const [isMinimized, setIsMinimized] = useState(false);
  // Auto-expand the recommended (current) step, but allow manual override
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Auto-expand recommended step when it changes (unless user has manually interacted)
  const effectiveExpandedStep = hasUserInteracted ? expandedStep : recommendedStep;

  // Reset user interaction when recommended step changes (user completed a step)
  // This allows auto-expand to kick in for the new recommended step
  useEffect(() => {
    setHasUserInteracted(false);
  }, [recommendedStep]);

  // Don't render if not eligible or still loading
  if (!showChecklist || isLoading) return null;

  const completedCount = completedSteps.length;
  const totalSteps = STEPS.length;
  const isAllComplete = completedCount === totalSteps;

  const isStepEnabled = (stepId: string): boolean => {
    if (stepId === 'start_project') return true;
    if (stepId === 'edit_project') return prerequisites.step1Completed;
    if (stepId === 'publish_share') return prerequisites.step2Completed;
    return false;
  };

  const isStepCompleted = (stepId: string): boolean => {
    return completedSteps.includes(stepId as any);
  };

  const handleStepClick = (stepId: string) => {
    // Allow toggling any step (including completed and locked)
    setHasUserInteracted(true);
    setExpandedStep(effectiveExpandedStep === stepId ? null : stepId);
  };

  const handleCtaClick = (href: string) => {
    navigate(href);
  };

  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-900 rounded-full shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all"
      >
        <div className="flex items-center gap-1.5">
          <div className="relative w-5 h-5">
            <svg className="w-5 h-5 -rotate-90" viewBox="0 0 20 20">
              <circle
                cx="10"
                cy="10"
                r="8"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="text-gray-200 dark:text-gray-700"
              />
              <circle
                cx="10"
                cy="10"
                r="8"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray={2 * Math.PI * 8}
                strokeDashoffset={2 * Math.PI * 8 * (1 - completedCount / totalSteps)}
                className="text-indigo-600 dark:text-indigo-400"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Setup guide
          </span>
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {completedCount}/{totalSteps}
        </span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[340px] bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <div className="relative w-8 h-8">
            <svg className="w-8 h-8 -rotate-90" viewBox="0 0 32 32">
              <circle
                cx="16"
                cy="16"
                r="14"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                className="text-gray-100 dark:text-gray-800"
              />
              <circle
                cx="16"
                cy="16"
                r="14"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeDasharray={2 * Math.PI * 14}
                strokeDashoffset={2 * Math.PI * 14 * (1 - completedCount / totalSteps)}
                className="text-indigo-600 dark:text-indigo-400 transition-all duration-500"
                strokeLinecap="round"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-gray-700 dark:text-gray-300">
              {completedCount}/{totalSteps}
            </span>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Setup guide
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {isAllComplete ? 'All done!' : `${totalSteps - completedCount} steps remaining`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized(true)}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
            title="Minimize"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <button
            onClick={dismiss}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Steps */}
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {STEPS.map((step, index) => {
          const completed = isStepCompleted(step.id);
          const enabled = isStepEnabled(step.id);
          const isExpanded = effectiveExpandedStep === step.id;

          return (
            <div key={step.id} className="relative">
              <button
                onClick={() => handleStepClick(step.id)}
                className={cn(
                  'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors',
                  'hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer'
                )}
              >
                {/* Step indicator */}
                <div className="flex-shrink-0 mt-0.5">
                  {completed ? (
                    <div className="w-5 h-5 rounded-full bg-indigo-600 dark:bg-indigo-500 flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    </div>
                  ) : (
                    <div
                      className={cn(
                        'w-5 h-5 rounded-full border-2 flex items-center justify-center text-xs font-medium',
                        enabled
                          ? 'border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                          : 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                      )}
                    >
                      {index + 1}
                    </div>
                  )}
                </div>

                {/* Step content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4
                      className={cn(
                        'text-sm font-medium',
                        completed
                          ? 'text-gray-500 dark:text-gray-400 line-through'
                          : enabled
                          ? 'text-gray-900 dark:text-white'
                          : 'text-gray-500 dark:text-gray-400'
                      )}
                    >
                      {step.title}
                    </h4>
                    {!enabled && !completed && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                        Locked
                      </span>
                    )}
                  </div>

                  {/* Only show description when expanded */}
                  {isExpanded && (
                    <p className={cn(
                      'text-xs mt-1',
                      enabled ? 'text-gray-500 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'
                    )}>
                      {step.description}
                    </p>
                  )}

                  {/* Expanded content with CTAs - only for enabled, non-completed steps */}
                  {isExpanded && enabled && !completed && (
                    <div className="mt-3 flex gap-2">
                      {step.ctas.map((cta, ctaIndex) => (
                        <Button
                          key={ctaIndex}
                          size="sm"
                          variant={cta.variant === 'secondary' ? 'outline' : 'default'}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCtaClick(cta.getHref(lastActiveProjectId));
                          }}
                          className={cn(
                            'h-8 px-3 text-xs',
                            cta.variant === 'secondary'
                              ? 'border-indigo-600 text-indigo-600 hover:bg-indigo-50 dark:border-indigo-400 dark:text-indigo-400 dark:hover:bg-indigo-900/20'
                              : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                          )}
                        >
                          {cta.label}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Expand/collapse indicator */}
                <div className="flex-shrink-0 mt-0.5">
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </button>
            </div>
          );
        })}
      </div>

      {/* Completion message */}
      {isAllComplete && (
        <div className="px-4 py-3 bg-indigo-50 dark:bg-indigo-900/20 border-t border-indigo-100 dark:border-indigo-800">
          <p className="text-sm text-indigo-700 dark:text-indigo-300 font-medium text-center">
            You're all set! Happy building.
          </p>
        </div>
      )}
    </div>
  );
}
